import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import {Alert, IconButton, Stack} from "@mui/material";
import { useSelector, dispatch } from "../../redux/store";
import { setOperatorInfo } from "../../redux/slices/user";
import { ANDROID_ONLY_ROLES, OPERATORS_GROUP } from "../../constants";
import { PATH_DASHBOARD } from "../../routes/path";
import { getPathAfterLogin } from "../../config";
import { useNavigate } from "react-router-dom";
import { fetchAllOperators } from "../../redux/slices/operators";
import { isPrimaryUser, waitForCurrentUser } from "../../auth/FirebaseContext";
import Joyride from 'react-joyride';
import Iconify from "../../components/Iconify";

export const OperatorPinInput = ({ onBack }) => {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const pinInputRef = useRef(null);
    const navigate = useNavigate();

    const { allOperators } = useSelector(state => state.operators);
    const [runTutorial, setRunTutorial] = useState(false);

    const steps = [
        {
            target: '.pin-input',
            content: 'Ovde unesite svoj PIN koji ste dobili.',
            disableBeacon: true,
            placement: 'bottom'
        },
        {
            target: '.confirm-button',
            content: 'Kliknite ovde da potvrdite unos.',
            placement: 'bottom'
        },
        {
            target: '.numpad',
            content: 'Možete koristiti i numeričku tastaturu za unos.',
            placement: 'top'
        },
        {
            target: '.logout-button',
            content: 'Kliknite ovde ako želite da se vratite.',
            placement: 'top'
        }
    ];

    useEffect(() => {
        dispatch(fetchAllOperators());
        pinInputRef.current?.focus();
    }, []);
    const handleChange = (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setPin(value);
        }
    };
    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');

        let operators = allOperators;
        try {
            const currentUser = await waitForCurrentUser();
            if (currentUser) {
                await currentUser.getIdToken(true); // force refresh tokena
            }
            const result = await dispatch(fetchAllOperators()).unwrap();
            if (Array.isArray(result)) {
                operators = result;
            }
        } catch (e) {
            console.error('fetchAllOperators (pin submit)', e);
            if (!Array.isArray(operators) || operators.length === 0) {
                setError(e?.message || 'Greška pri učitavanju operatera. Pokušajte ponovo.');
                setPin('');
                pinInputRef.current?.focus();
                return;
            }
        }

        const operatorFound = operators.find(o => String(o.secret) === pin && !o.inactive);

        if (operatorFound && ANDROID_ONLY_ROLES.includes(Number(operatorFound.group))) {
            setError("Ova uloga radi samo u Android aplikaciji, prijava na web nije dozvoljena.");
            setPin('');
            pinInputRef.current?.focus();
            return;
        }

        if (operatorFound) {
            await dispatch(setOperatorInfo(operatorFound));
            if (operatorFound.group === OPERATORS_GROUP.dispatcher) {
                return navigate(PATH_DASHBOARD.otpravnik.list, { replace: true });
            }
            return navigate(getPathAfterLogin(operatorFound, isPrimaryUser()), { replace: true });
        } else {
            setError("Lozinka nije validna!");
            setPin('');
            pinInputRef.current?.focus();
        }
    };

    const handleDigitClick = (digit) => {
        setPin((prev) => prev + digit);
        pinInputRef.current?.focus();
    };

    const handleBackspaceClick = () => {
        setPin((prev) => prev.slice(0, -1));
        pinInputRef.current?.focus();
    };

    return (
        <Box sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
        }}>
            <Joyride
                steps={steps}
                run={runTutorial}
                continuous
                showSkipButton
                showProgress
                locale={{
                    back: 'Nazad',
                    close: 'Zatvori',
                    last: 'Završi',
                    next: 'Dalje',
                    skip: 'Preskoči',
                    tooltip: {
                        step: (current, total) => `Korak ${current} od ${total}`
                    }
                }}
                styles={{
                    options: {
                        primaryColor: '#1976d2',
                        zIndex: 1300
                    }
                }}
                callback={(data) => {
                    if (data.status === 'finished' || data.status === 'skipped') {
                        setRunTutorial(false);
                    }
                }}
            />


            <Typography textAlign="center" variant="h4" color="text.primary" fontWeight="600">
                Prijavljivanje operatera na rad
            </Typography>
            <Typography component="p" color="text.secondary" sx={{ mt: 1, mb: 4 }}>
                Unesite vaš PIN ili skenirajte karticu.
            </Typography>

            <Box component="form" noValidate onSubmit={handleSubmit}
                 sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                {!!error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

                <Stack spacing={1} sx={{ width: '100%', mb: 3 }}>
                    <TextField
                        inputRef={pinInputRef}
                        type="password"
                        value={pin}
                        size="normal"
                        onChange={handleChange}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            style: {
                                textAlign: 'center',
                                fontSize: '1.2rem',
                                letterSpacing: '0.3rem'
                            }
                        }}
                        placeholder="Unesite PIN"
                        fullWidth
                        className="pin-input"
                    />
                </Stack>

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={pin.length < 4}
                    sx={{ mb: 2, py: 1.5 }}
                    className="confirm-button"
                >
                    Potvrdi
                </Button>

                <Button
                    onClick={onBack}
                    fullWidth
                    variant="outlined"
                    className="logout-button"
                >
                    Odjavi se
                </Button>

                <Button
                    onClick={() => setRunTutorial(true)}
                    variant="text"
                    size="small"
                    sx={{ mt: 2 }}
                >
                    Pomoć (Prikaži uputstvo)
                </Button>
            </Box>

            {/* Numerička tastatura */}
            <Box
                className="numpad"
                sx={{ mt: 4, display: 'grid', gridTemplateColumns: 'repeat(3, 112px)', gap: 1 }}
            >
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'BRIŠI', '0', 'POTVRDI'].map((val, index) => {
                    if (val === 'BRIŠI') {
                        return (
                            <Button
                                key={index}
                                variant="contained"
                                color="error"
                                onClick={handleBackspaceClick}
                                sx={{
                                    fontSize: '1.5rem',
                                    height: '60px',
                                    minWidth: '60px',
                                    gridColumn: '1',
                                    px: 0
                                }}
                            >
                                <Iconify icon="material-symbols:backspace-rounded" width={28} height={28} />
                            </Button>
                        );
                    }

                    if (val === 'POTVRDI') {
                        return (
                            <Button
                                key={index}
                                variant="contained"
                                color="success"
                                onClick={(e) => handleSubmit(new Event('submit'))}
                                sx={{
                                    fontSize: '1.5rem',
                                    height: '60px',
                                    minWidth: '60px',
                                    gridColumn: '3',
                                    px: 0
                                }}
                            >
                                <Iconify icon="mi:enter" width={24} height={24} />
                            </Button>

                        );
                    }

                    return (
                        <Button
                            key={index}
                            variant="outlined"
                            color="primary"
                            onClick={() => handleDigitClick(val)}
                            sx={{
                                fontSize: '1.5rem',
                                height: '60px',
                                minWidth: '60px'
                            }}
                        >
                            {val}
                        </Button>
                    );
                })}
            </Box>


        </Box>
    );
};
